import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { setSession } from "@/lib/sessionStore";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DemoAutoLogin — Página de entrada do fluxo "Iniciar Demonstração".
 *
 * O rep clica no botão no seu portal, o backend gera um JWT single-use
 * curto-prazo e nos redireciona pra cá com `?token=...&session=...`.
 *
 * Fluxo:
 *  1. Lê `token` e `session` da URL.
 *  2. Chama POST /demo/auto-login-consume — queima o token single-use.
 *  3. Salva o JWT retornado no localStorage.
 *  4. Limpa a URL via `history.replaceState` (não deixa token no histórico).
 *  5. Redireciona pro dashboard.
 */
export default function DemoAutoLogin() {
  const nav = useNavigate();
  const { completeAutoLogin } = useAuth();
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("Iniciando modo demonstração...");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const session = params.get("session");

    if (!token || !session) {
      setError("Link de demonstração inválido — token ausente.");
      return;
    }

    // Limpa a URL IMEDIATAMENTE, antes mesmo do consume. Se o consume
    // falhar, o token ainda estará no localStorage do request. Melhor
    // limpar antes pra garantir que a URL nunca fica no histórico.
    try {
      window.history.replaceState({}, "", "/demo-loading");
    } catch (_) { /* noop */ }

    (async () => {
      try {
        setStatus("Validando credenciais...");
        const r = await axios.post(`${API}/demo/auto-login-consume`, {
          token, session_id: session,
        });
        const data = r.data || {};
        if (!data.token) throw new Error("token ausente na resposta");

        // Persiste JWT + metadados de sessão demo via sessionStore
        // (fonte-única-de-verdade — usa `agentos_token`, `agentos_user`, etc.)
        // IMPORTANTE: precisa incluir `role` no user, senão `canAccess()`
        // do frontend filtra todos os itens do sidebar (bug real: o rep
        // só via "Dashboard" no menu).
        const userObj = data.user || {
          email: data.email,
          id: data.email,
          role: "owner",
          tenant_id: data.tenant_id,
        };
        const tenantObj = data.tenant || {
          id: data.tenant_id, is_demo_tenant: true,
        };
        setSession({ token: data.token, user: userObj, tenant: tenantObj });
        // ⚠️ AuthContext SÓ lê localStorage no mount inicial (useEffect com
        // deps vazio). Sem `completeAutoLogin`, useAuth().user fica null
        // quando o nav("/app") monta o AppLayout, o RequireAuth vê user=null
        // e redireciona pra /login. Bug real de "está deslogando".
        completeAutoLogin(data.token, userObj, tenantObj);
        localStorage.setItem("demo_session_id", session);
        localStorage.setItem("is_demo_mode", "true");

        setStatus("Preparando ambiente...");
        // Redireciona pro app (não pro dashboard direto — deixa o
        // roteador do AppShell decidir)
        nav("/app", { replace: true });
      } catch (err) {
        console.error("[demo auto-login]", err);
        const detail = err?.response?.data?.detail;
        const msg = typeof detail === "string" ? detail
                  : detail?.reason === "already_consumed"
                    ? "Este link já foi utilizado. Gere um novo no portal do representante."
                    : detail?.reason === "invalid_token"
                      ? "Token inválido ou expirado. Gere um novo no portal do representante."
                      : "Não foi possível iniciar a demonstração.";
        setError(msg);
      }
    })();
  }, [nav]);

  return (
    <div
      data-testid="demo-auto-login-page"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#0a0a0a",
        color: "#f5f5f5",
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}
    >
      <div style={{ textAlign: "center", padding: 32, maxWidth: 480 }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>🎬</div>
        <h1 style={{ fontSize: 24, marginBottom: 12, fontWeight: 500 }}>
          Modo Demonstração AgentUS
        </h1>
        {error ? (
          <>
            <p style={{ color: "#ef4444", marginBottom: 24 }} data-testid="demo-login-error">
              {error}
            </p>
            <button
              data-testid="demo-back-btn"
              onClick={() => nav("/representante/portal", { replace: true })}
              style={{
                padding: "10px 24px",
                background: "#f5f5f5",
                color: "#0a0a0a",
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              Voltar ao portal
            </button>
          </>
        ) : (
          <p style={{ color: "#a3a3a3" }} data-testid="demo-login-status">
            {status}
          </p>
        )}
      </div>
    </div>
  );
}
